// Google Consent Mode v2 helpers.
//
// Single source of truth for the consent storage key and the consent-signal
// mapping. The default-denied state is set by an inline script built from
// these constants in util/includeScripts.js, BEFORE gtm.js loads. The cookie
// banner calls updateGtagConsent() on user interaction.
//
// NOTE: GTM's consent API only understands `arguments`-object pushes (the
// gtag() pattern), not plain object literals — so pushEvent from dataLayer.js
// cannot be reused here. We also deliberately do NOT depend on or define
// window.gtag: it doesn't exist outside production (the GTM loader is gated
// on the production env) and the dormant gtag.js path has its own conventions.

const isBrowser = () => typeof window !== 'undefined';

// Existing key — kept so returning users' stored choices survive.
export const CONSENT_STORAGE_KEY = 'cookieConsentGiven';

// Consent must never break the app: swallow failures and return a fallback.
const safe = (fn, fallback) => (...args) => {
  try {
    return fn(...args);
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[analytics] consent helper failed:', e);
    }
    return typeof fallback === 'function' ? fallback() : fallback;
  }
};

// The four Consent Mode v2 signals, all granted or all denied (binary banner).
export const consentSignals = granted => {
  const value = granted ? 'granted' : 'denied';
  return {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  };
};

export const getStoredConsent = safe(() => {
  if (!isBrowser()) {
    return null;
  }
  const stored = window.localStorage.getItem(CONSENT_STORAGE_KEY);
  return stored === 'true' ? 'granted' : stored === 'false' ? 'denied' : null;
}, null);

export const storeConsent = safe(granted => {
  if (!isBrowser()) {
    return;
  }
  window.localStorage.setItem(CONSENT_STORAGE_KEY, granted ? 'true' : 'false');
});

export const updateGtagConsent = safe(granted => {
  if (!isBrowser()) {
    return;
  }
  function gtag() {
    (window.dataLayer = window.dataLayer || []).push(arguments);
  }
  gtag('consent', 'update', consentSignals(granted));
});
